import type { AxiosError, AxiosInstance, AxiosRequestConfig } from "axios";
import axios from "axios";
import store from "@/store";
import { objectToFilterString } from "@/utils/apiFilter";

export const ResponseStatus = {
  Ok: "OK",
  Conflict: "CONFLICTED_ERROR",
  BadRequest: "BAD_REQUEST_ERROR",
} as const;

export type Response<T> = {
  status: string;
  result?: T;
  errorMessage?: string;
};

class ApiClient {
  private axiosInstance: AxiosInstance;

  constructor(accessToken = "") {
    if (!process.env.VUE_APP_API_URL) {
      throw new Error("API_URL is not defined");
    }

    this.axiosInstance = this.initialize(
      process.env.VUE_APP_API_URL,
      accessToken,
    );
  }

  private initialize(baseURL: string, accessToken: string): AxiosInstance {
    const axiosInstance = axios.create({ baseURL });

    // Add response interceptor to handle 401 Unauthorized globally
    axiosInstance.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        if (
          error.response?.status === 401 &&
          !error.config?.url?.includes("/auth/exchange")
        ) {
          console.warn(
            "[API] Received 401 Unauthorized, session expired - signing out user",
          );
          await store.dispatch("signOut", "session_expired");
        }
        return Promise.reject(error);
      },
    );

    if (accessToken) {
      this.setAccessToken(accessToken);
    }

    return axiosInstance;
  }

  setAccessToken(accessToken: string): void {
    this.axiosInstance.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
  }

  destroy(): void {
    this.axiosInstance.defaults.headers.common.Authorization = "";
  }

  private async request<R = unknown>(
    method: "get" | "post" | "put" | "delete",
    path: string,
    options: AxiosRequestConfig = {},
  ): Promise<Response<R>> {
    try {
      // Wait for authentication to be ready before making the request
      // Skip waiting for the auth exchange endpoint itself
      if (!path.includes("/auth/exchange")) {
        // Check if we already have an access token set
        const hasAccessToken =
          this.axiosInstance.defaults.headers.common.Authorization;
        if (!hasAccessToken) {
          // Wait for authentication status to be resolved
          // Poll with a timeout to avoid infinite waiting
          const maxWaitTime = 10000; // 10 seconds max
          const pollInterval = 50; // Check every 50ms
          const startTime = Date.now();

          while (store.state.authStatus === "pending") {
            if (Date.now() - startTime > maxWaitTime) {
              console.warn(
                "[API] Authentication timeout, proceeding with request",
              );
              break;
            }
            // Wait for a short interval before checking again
            await new Promise((resolve) => setTimeout(resolve, pollInterval));
          }

          // If authentication failed, log a warning but still attempt the request
          // (it will fail with proper error handling from the server)
          if (store.state.authStatus === "unauthenticated") {
            console.warn(
              "[API] Authentication failed, request may be unauthorized",
            );
          }
        }
      }

      const response = await this.axiosInstance.request<Response<R>>({
        url: path,
        method,
        paramsSerializer: (params) => objectToFilterString(params),
        ...options,
      });
      return response.data;
    } catch (error) {
      const errorResponse = error as AxiosError<Response<R>>;
      console.error(
        `[${method.toUpperCase()}] ${path}:`,
        errorResponse?.response?.data ?? errorResponse,
      );
      return errorResponse?.response?.data as Response<R>;
    }
  }

  async get<R = unknown, P = { [key: string]: unknown }>(
    path: string,
    params?: P,
    options?: AxiosRequestConfig,
  ): Promise<Response<R>> {
    return this.request("get", path, { params, ...options });
  }

  async post<R = unknown, I = unknown>(
    path: string,
    body?: Partial<I>,
    options?: AxiosRequestConfig,
  ): Promise<Response<R>> {
    return this.request("post", path, { data: body, ...options });
  }

  async put<R = unknown, I = unknown>(
    path: string,
    body?: Partial<I>,
    options?: AxiosRequestConfig,
  ): Promise<Response<R>> {
    return this.request("put", path, { data: body, ...options });
  }

  async delete<R = unknown>(
    path: string,
    options?: AxiosRequestConfig,
  ): Promise<Response<R>> {
    return this.request("delete", path, options);
  }
}

export const apiClient = new ApiClient();
